import React from 'react';

export interface SocialFormatOption {
  id: string;
  platform: 'linkedin' | 'x';
  label: string;
  description: string;
  emoji: string;
  renderer: 'text' | 'carousel' | 'thread' | 'poll';
  limits: Record<string, unknown>;
}

interface PostTypePickerProps {
  formats: SocialFormatOption[];
  /** Filter the grid by platform. Empty array shows all. */
  platform: 'linkedin' | 'x' | null;
  /** Currently-selected format id (single-select mode). */
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** When true, switches to multi-select mode (for cross-post #110). The
   *  parent owns the array via selectedIds + onToggle. selectedId / onSelect
   *  are ignored in this mode. */
  multi?: boolean;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
}

/**
 * Grid of format cards. Single-select by default; multi-select when
 * `multi={true}`. The empty state ("no formats loaded yet") is handled by the
 * parent view.
 */
export default function PostTypePicker({ formats, platform, selectedId, onSelect, multi = false, selectedIds = [], onToggle }: PostTypePickerProps) {
  const filtered = platform ? formats.filter((f) => f.platform === platform) : formats;
  const selected = new Set(selectedIds);
  return (
    <div
      className="post-type-picker"
      role={multi ? "group" : "radiogroup"}
      aria-label="Social post format"
      aria-multiselectable={multi ? true : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {filtered.map((f) => {
        const isSelected = multi ? selected.has(f.id) : f.id === selectedId;
        return (
          <button
            key={f.id}
            type="button"
            role={multi ? "checkbox" : "radio"}
            aria-checked={isSelected}
            data-format-id={f.id}
            onClick={() => {
              if (multi) onToggle?.(f.id);
              else onSelect(f.id);
            }}
            className={`post-type-card ${isSelected ? 'post-type-card--selected' : ''}`}
            style={{
              textAlign: 'left',
              padding: 14,
              borderRadius: 0,
              border: isSelected ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: isSelected ? 'var(--accent-light)' : 'var(--bg-surface)',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }} aria-hidden>{f.emoji}</span>
              <span style={{ fontWeight: 600, flex: 1 }}>{f.label}</span>
              {multi && (
                <span
                  aria-hidden
                  style={{
                    width: 14, height: 14, border: '1px solid var(--ink)',
                    background: isSelected ? 'var(--ink)' : 'transparent',
                    color: 'var(--paper)', fontSize: 10, lineHeight: '12px', textAlign: 'center',
                  }}
                >{isSelected ? '✓' : ''}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.description}</div>
          </button>
        );
      })}
    </div>
  );
}
